import React from "react";
import styled from "@emotion/styled";
import whatsappBg from "../../assets/wa_bg.png";
import noImage from "../../assets/no-image.jpg";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Typography } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import VideoPreview from "../../widgets/VideoPreview";

const ChatContainer = styled.div`
  max-width: 420px;
  height: 640px;
  background: #e5ddd5 url(${whatsappBg});
  background-size: contain;
  font-family: "Segoe UI", Helvetica, Arial, sans-serif;
  border: 1px solid #e9e9e9;
  border-radius: 8px;
  overflow: hidden;
  margin: 24px auto;
  display: flex;
  flex-direction: column;
`;

const ChatHeader = styled.div`
  height: 52px;
  flex-shrink: 0;
  background: #075e54;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  font-weight: 500;
  font-size: 15px;
  gap: 12px;
`;

const ChatContent = styled.div`
  flex: 1;
  min-height: 0;
  padding: 20px 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const Message = styled.div`
  align-self: flex-start;
  max-width: 82%;
  min-width: 0;
  margin-bottom: 10px;
`;

const Bubble = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 12px 14px;
  box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);
  position: relative;
  overflow-wrap: anywhere;

  &::before {
    content: "";
    position: absolute;
    border-right: 16px solid #fff;
    border-bottom: 10px solid transparent;
    left: -8px;
    top: 0;
  }
`;

const HeaderImage = styled.img`
  width: 100%;
  border-radius: 4px;
  object-fit: cover;
`;

const MediaShell = styled.div`
  width: 100%;
  height: 180px;
  background: #f2f2f2;
  border-radius: 4px;
  color: #555;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
`;

export const BodyText = styled.div`
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.4;
  color: #303030;
`;

const FooterText = styled.div`
  font-size: 11px;
  color: #6b6b6b;
  margin-top: 12px;
  text-align: right;
`;

const ButtonWrap = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
  gap: 6px;
`;

const ChatButton = styled.a`
  background: #f8f8f8;
  border: none;
  border-radius: 18px;
  padding: 8px 16px;
  font-family: inherit;
  font-weight: 500;
  font-size: 13px;
  color: #075e54;
  text-align: center;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #e2e2e2;
  }
`;

const CarouselContainer = styled.div`
  display: flex;
  overflow-x: auto;
  margin-top: 12px;
  gap: 8px;
  padding-bottom: 8px;
`;

const Card = styled.div`
  min-width: 200px;
  background: #f8f8f8;
  border-radius: 8px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);
`;

const TemplateMeta = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-top: 1px solid #e9e9e9;
  font-size: 11px;
  padding: 8px 16px;
  color: #666;
  line-height: 1.3;
  overflow-wrap: anywhere;
`;

const TemplatePreview = ({ template, previewUrl = "", fileName = "" }) => {
  const renderComponent = (c, options = {}) => {
    const { overridePreviewUrl } = options;

    switch (c.type?.toUpperCase()) {
      case "HEADER": {
        const src = overridePreviewUrl || previewUrl || noImage;
        const format = c.format?.toUpperCase();
        if (format === "IMAGE") {
          return (
            <HeaderImage
              src={src}
              alt="header media"
              onError={(event) => {
                const image = event.currentTarget;
                if (image.getAttribute("src") !== noImage) image.src = noImage;
              }}
            />
          );
        }
        if (format === "VIDEO") {
          return <VideoPreview viewUrl={src} />;
        }
        if (format === "DOCUMENT") {
          const documentSrc = overridePreviewUrl || previewUrl || "";
          const filename =
            fileName ||
            c.filename ||
            documentSrc.split("?")[0].split("/").pop() ||
            "document.pdf";
          if (documentSrc) {
            return (
              <div>
                <MediaShell style={{ padding: 0 }}>
                  <iframe
                    src={documentSrc}
                    title="Document Preview"
                    width="100%"
                    height="100%"
                    style={{ border: "none", borderRadius: "4px" }}
                  />
                </MediaShell>
                <Typography
                  variant="caption"
                  sx={{ display: "block", textAlign: "center", mt: 0.5 }}
                >
                  <InsertDriveFileIcon fontSize="inherit" /> {filename}
                </Typography>
              </div>
            );
          }
          return (
            <MediaShell>
              <InsertDriveFileIcon /> &nbsp; {filename}
            </MediaShell>
          );
        }
        if (format === "LOCATION") {
          return (
            <MediaShell>
              <LocationOnOutlinedIcon />
            </MediaShell>
          );
        }
        return (
          <Typography fontWeight="bold" fontSize="16px">
            {c.text}
          </Typography>
        );
      }
      case "BODY":
        return <BodyText>{c.text}</BodyText>;
      case "FOOTER":
        return <FooterText>{c.text}</FooterText>;
      case "BUTTONS":
        return (
          <ButtonWrap>
            {c.buttons?.map((btn, index) => {
              const type = btn.type?.toUpperCase();
              if (type === "VOICE_CALL") {
                return (
                  <ChatButton
                    key={index}
                    as="button"
                    type="button"
                    title="Preview only. Customers can use this button in the delivered WhatsApp message."
                  >
                    <WhatsAppIcon sx={{ fontSize: 17, flexShrink: 0 }} />
                    {btn.text || "Call on WhatsApp"}
                  </ChatButton>
                );
              }
              let href = "#";
              if (type === "PHONE_NUMBER") href = `tel:${btn.phone_number}`;
              if (type === "URL") href = btn.url;
              return (
                <ChatButton
                  key={index}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {type === "COPY_CODE" ? "Copy Code" : btn.text}
                </ChatButton>
              );
            })}
          </ButtonWrap>
        );
      case "CAROUSEL":
        return (
          <CarouselContainer>
            {c.cards?.map((card, index) => (
              <Card key={index}>
                {card.components?.map((comp, compIndex) => (
                  <React.Fragment key={compIndex}>
                    {renderComponent(comp, {
                      overridePreviewUrl: comp.example?.header_handle?.[0],
                    })}
                  </React.Fragment>
                ))}
              </Card>
            ))}
          </CarouselContainer>
        );
      default:
        return null;
    }
  };

  return (
    <ChatContainer>
      <ChatHeader>
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {template?.name}
        </span>
        <span style={{ fontSize: 12 }}>Preview</span>
      </ChatHeader>
      <ChatContent>
        <Message>
          <Bubble>
            {template?.components?.map((c, index) => (
              <React.Fragment key={index}>{renderComponent(c)}</React.Fragment>
            ))}
          </Bubble>
        </Message>
      </ChatContent>
      <TemplateMeta>
        {template?.name} &nbsp;|&nbsp; {template?.id} &nbsp;|&nbsp;{" "}
        {template?.language} &nbsp;|&nbsp; {template?.category ?? "—"}{" "}
        &nbsp;|&nbsp; {template?.status}
      </TemplateMeta>
    </ChatContainer>
  );
};

export default TemplatePreview;
